import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Table from "components/DesignSystem/Table/V2/Table";
import TransactionColumn from "components/Transaction/TransactionColumn";
import { AddTransactionInvoice } from "components/Transaction/AddTransactionInvoice";
import { AddTransactionComment } from "components/Transaction/AddTransactionComment";
import { AddTransactionMemo } from "components/Transaction/AddTransactionMemo";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Loader from "components/DesignSystem/Loader/loader";
import { TableUI } from "components/design/TableUI";
import { Cross } from "components/icons/Cross";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetCategorisationTxnsQuery } from "store/apis/categorisationHistory";
import { Transactions } from "types/Models/books";
import { Button } from "components/DesignSystem/Button/Button";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

const columnHelper = createColumnHelper<Transactions>();

const COLUMN_IDS = {
  DATE: "DATE",
  DESCRIPTION: "DESCRIPTION",
  AMOUNT: "AMOUNT",
  ATTACHMENTS: "ATTACHMENTS",
} as const;

const buildColumns = (hasWritePermission: boolean) => [
  columnHelper.accessor("transaction.date", {
    id: COLUMN_IDS.DATE,
    size: 20,
    header: "Date",
    cell: (info) => (
      <div className="t-text-body-sm t-text-text-60">
        {dayjs(info.getValue()).format(DISPLAY_DATE)}
      </div>
    ),
  }),

  columnHelper.accessor("transaction.description", {
    id: COLUMN_IDS.DESCRIPTION,
    enableSorting: false,
    size: 40,
    header: "Description",
    cell: (info) => {
      const description = info.getValue();
      return (
        <div className="t-w-full t-overflow-hidden">
          <ConditionalToolTip condition={description}>
            <span className="t-text-body-sm t-text-text-60 t-inline-block t-max-w-full t-truncate">
              {description || "-"}
            </span>
          </ConditionalToolTip>
        </div>
      );
    },
  }),

  columnHelper.accessor("transaction.amount", {
    id: COLUMN_IDS.AMOUNT,
    size: 20,
    header: () => (
      <span className="t-flex t-justify-end t-ml-auto">Amount</span>
    ),
    cell: (info) => {
      const amount = info.getValue();

      return <TransactionColumn.Amount amount={amount} />;
    },
  }),

  columnHelper.display({
    id: COLUMN_IDS.ATTACHMENTS,
    size: 20,
    header: "Attachments",
    cell: ({ row }) => (
      <div className="t-flex t-items-center t-gap-1">
        <AddTransactionInvoice
          transactionDetails={row.original}
          zIndexClass="t-z-signBox"
          hasWritePermission={hasWritePermission}
        />
        <AddTransactionMemo
          transactionDetails={row.original}
          zIndexClass="t-z-modal-footer"
          hasWritePermission={hasWritePermission}
        />
        <AddTransactionComment
          transactionDetails={row.original}
          zIndexClass="t-z-modal-footer"
          hasWritePermission={hasWritePermission}
        />
      </div>
    ),
  }),
];

type CategorisationTxnTableProps = {
  merchantId: string;
  categoryId: string;
  onRowClick?: (transactionId: string) => void;
  isReadOnly?: boolean;
  onClose?: () => void;
  startDate?: string;
  endDate?: string;
};

export const CategorisationTxnTable = ({
  merchantId,
  categoryId,
  onRowClick,
  isReadOnly = false,
  onClose,
  startDate,
  endDate,
}: CategorisationTxnTableProps) => {
  const { uuid: groupId } = useCurrentGroup();
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );
  const [pageNum, setPageNum] = useState(1);

  const { data, isLoading, isSuccess } = useGetCategorisationTxnsQuery(
    {
      merchantId,
      categoryId,
      pageNum,
      groupId: groupId!,
      entityId: entityId!,
      startDate,
      endDate,
    },
    {
      skip: !merchantId || !categoryId || !entityId,
    }
  );

  const {
    transactions = [],
    total_count = 0,
    total_pages = 1,
    per_page = 1,
  } = data || {};

  const isEmpty = transactions.length === 0;

  const table = useReactTable({
    data: transactions,
    columns: buildColumns(hasWritePermission),
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const handleRowClick =
    onRowClick && !isReadOnly
      ? (row: { original: Transactions }) =>
          onRowClick(row.original.transaction.uuid)
      : undefined;

  return (
    <Async.Root
      isLoading={isLoading}
      isEmpty={isEmpty}
      isSuccess={isSuccess}
      loaderType="secondary"
      customLoader={
        <div className="t-w-full t-flex t-justify-center t-py-4">
          <div className="t-h-4 t-w-4">
            <Loader customType="secondary" />
          </div>
        </div>
      }
    >
      <Async.Empty>
        <div className="t-text-text-60 t-text-body-sm t-text-center t-w-full t-p-4">
          No transactions found
        </div>
      </Async.Empty>
      <Async.Success>
        <div className="t-border-l-2 t-border-solid t-border-neutral-20 t-border-0 t-px-4 t-pt-4">
          <div className="t-flex t-items-center t-justify-between t-mb-1">
            <span className="t-text-subtext-sm t-text-text-60">
              {total_count} Transactions:
            </span>
            <div className="t-flex t-items-center t-gap-2">
              <PaginationHandler
                page_num={pageNum}
                per_page={per_page}
                total_count={total_count}
                total_pages={total_pages}
                onPageNumChange={setPageNum}
              />

              {onClose && (
                <Button customType="ghost_icon" size="xsmall" onClick={onClose}>
                  <Cross size="16" />
                </Button>
              )}
            </div>
          </div>
          <TableUI
            table={table}
            isHeaderCellUpperCase={false}
            onRowClick={handleRowClick}
            sticky={false}
            layout="fixed"
          >
            {({ row }) => (
              <Table.Row
                key={row.id}
                className={classNames("t-group", {
                  "t-cursor-pointer": handleRowClick,
                })}
                onRowClick={
                  handleRowClick ? () => handleRowClick(row) : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}%` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            )}
          </TableUI>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
